import { useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { EyeIcon, EyeOffIcon, LockIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Field, FieldError, FieldGroup, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";
import { ScandiWordmark } from "@/components/brand/scandi-wordmark";
import { LanguageToggle } from "@/components/layout/language-toggle";
import { ThemeToggle } from "@/components/layout/theme-toggle";
import { useGoogleSignIn, useLogin } from "@/hooks/use-session";
import { api } from "@/lib/api";
import { useErrorMessage } from "@/lib/errors";
import { GoogleSignInError, prepareGoogleSignIn, type GoogleConfig } from "@/features/auth/firebase";

/**
 * The way in.
 *
 * Google comes first because that is where everyone is headed: a personal
 * account the portal can tell apart from everyone else's. The team password
 * stays underneath, always rendered and never folded away, until the last
 * person has moved over, and because the screenshot and overflow scripts sign
 * in by filling `#password` as the first input on the page.
 */
export function LoginPage() {
  const { t } = useTranslation();
  const config = useQuery({
    queryKey: ["auth", "config"],
    queryFn: api.authConfig,
    // Set per deployment; it does not change under an open tab.
    staleTime: Infinity,
    retry: false,
  });
  const google = config.data?.google ?? null;

  return (
    <div className="relative flex min-h-svh flex-col items-center justify-center overflow-hidden p-4">
      <BackgroundGlow />

      <div className="absolute top-4 right-4 flex items-center gap-1">
        <LanguageToggle />
        <ThemeToggle />
      </div>

      <div className="flex w-full max-w-sm flex-col gap-8">
        <div className="flex flex-col items-center gap-3">
          <ScandiWordmark className="h-9" gradient />
          <p className="text-muted-foreground text-sm tracking-wide uppercase">
            {t("nav.portalName")}
          </p>
        </div>

        <Card className="shadow-lg shadow-black/5">
          <CardHeader>
            <CardTitle>{google ? t("auth.title") : t("login.title")}</CardTitle>
            <CardDescription>
              {google ? t("auth.description") : t("login.description")}
            </CardDescription>
          </CardHeader>

          {config.isLoading ? (
            <CardContent>
              {/* Holds the button's place while the config loads. */}
              <Skeleton className="h-9 w-full rounded-md" />
            </CardContent>
          ) : google ? (
            <GoogleSection config={google} />
          ) : (
            // Team passwords are no longer offered once Google sign-in is on:
            // everyone gets a personal account. The form only remains as the way
            // into an instance where Google sign-in is not configured.
            <PasswordForm showPhaseOut={false} />
          )}
        </Card>

        {/* The Vite dev server only: compiled out of every production build. The
            API serves the page only when it runs with ENABLE_DEV_SIGN_IN=true
            (apps/api/src/routes/dev-sign-in.ts); otherwise the path does not exist. */}
        {import.meta.env.DEV && (
          <a
            href="/api/dev/sign-in"
            className="text-muted-foreground text-center text-xs underline-offset-4 hover:underline"
          >
            Development sign-in
          </a>
        )}
      </div>
    </div>
  );
}

function GoogleSection({ config }: { config: GoogleConfig }) {
  const { t } = useTranslation();
  const signIn = useGoogleSignIn();
  const describe = useErrorMessage();
  // Load the SDK and set up Auth before the button can be pressed: a click that
  // has to wait for the download runs signInWithPopup outside the browser's
  // short user-activation window, and the popup is blocked (see
  // prepareGoogleSignIn). A query rather than an effect so a failed download
  // has a retry and is shared by every render instead of started twice.
  const ready = useQuery({
    queryKey: ["auth", "google-ready", config.projectId, config.apiKey],
    queryFn: () => prepareGoogleSignIn(config).then(() => true),
    staleTime: Infinity,
    retry: false,
  });

  const error = signIn.error;
  const errorMessage =
    error instanceof GoogleSignInError
      ? error.reason === "popup_blocked"
        ? t("auth.popupBlocked")
        : t("auth.googleFailed")
      : describe(error);

  return (
    <CardContent className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <Button
          // Never a submit button: Enter in the password field must submit the
          // password, and the automation scripts click the page's submit button.
          type="button"
          variant="outline"
          size="lg"
          className="bg-background w-full"
          disabled={signIn.isPending || !ready.isSuccess}
          onClick={() => signIn.mutate(config)}
        >
          {signIn.isPending || ready.isPending ? (
            <Spinner data-icon="inline-start" />
          ) : (
            <GoogleMark data-icon="inline-start" />
          )}
          {signIn.isPending
            ? t("auth.waitingForGoogle")
            : ready.isPending
              ? t("auth.preparingGoogle")
              : t("auth.continueWithGoogle")}
        </Button>
        {ready.isError && (
          <div role="alert" className="flex flex-col items-center gap-1 text-center">
            <p className="text-destructive text-sm">{t("auth.googleUnavailable")}</p>
            <Button
              type="button"
              variant="link"
              size="sm"
              disabled={ready.isFetching}
              onClick={() => void ready.refetch()}
            >
              {ready.isFetching && <Spinner data-icon="inline-start" />}
              {t("auth.retryGoogle")}
            </Button>
          </div>
        )}
        {errorMessage && (
          <p role="alert" className="text-destructive text-center text-sm">
            {errorMessage}
          </p>
        )}
        {/* A popup that closes without a result is usually someone changing their
            mind, but it is also exactly how a broken sign-in looks from here. Saying
            so beats a button that silently does nothing. */}
        {signIn.isSuccess && signIn.data === null && (
          <p role="status" className="text-muted-foreground text-center text-sm">
            {t("auth.googleClosed")}
          </p>
        )}
      </div>

    </CardContent>
  );
}

function PasswordForm({ showPhaseOut }: { showPhaseOut: boolean }) {
  const { t } = useTranslation();
  const [password, setPassword] = useState("");
  const [visible, setVisible] = useState(false);
  const login = useLogin();
  const errorMessage = useErrorMessage()(login.error);
  const field = useRef<HTMLInputElement>(null);

  function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    // The button stays pressable while the field is empty; pressing it then
    // points at the field rather than doing nothing.
    if (password.trim()) login.mutate(password);
    else field.current?.focus();
  }

  return (
    <form onSubmit={onSubmit}>
      <CardContent>
        <FieldGroup>
          <Field data-invalid={errorMessage ? true : undefined}>
            <FieldLabel htmlFor="password">{t("login.password")}</FieldLabel>
            <div className="relative">
              <LockIcon className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
              <Input
                ref={field}
                id="password"
                name="password"
                type={visible ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                // Chrome only offers to save the password when it can see a
                // password field with a matching autocomplete hint.
                autoComplete="current-password"
                autoFocus
                aria-invalid={errorMessage ? true : undefined}
                className="px-9"
                placeholder="••••••••••"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute top-1/2 right-1 size-7 -translate-y-1/2"
                onClick={() => setVisible((v) => !v)}
                aria-label={visible ? t("login.hidePassword") : t("login.showPassword")}
              >
                {visible ? <EyeOffIcon /> : <EyeIcon />}
              </Button>
            </div>
            {errorMessage && <FieldError>{errorMessage}</FieldError>}
          </Field>
        </FieldGroup>
      </CardContent>

      <CardFooter className="mt-6 flex-col gap-3">
        <Button
          type="submit"
          variant={showPhaseOut ? "secondary" : "default"}
          className="w-full"
          disabled={login.isPending}
        >
          {login.isPending && <Spinner data-icon="inline-start" />}
          {login.isPending ? t("login.submitting") : t("login.submit")}
        </Button>
        <p className="text-muted-foreground text-center text-xs text-balance">
          {showPhaseOut ? t("auth.passwordPhaseOut") : t("login.staySignedIn")}
        </p>
      </CardFooter>
    </form>
  );
}

/**
 * Google's "G", in its own colours as Google's brand rules require for a
 * sign-in button. Inline so the login screen needs nothing from Google's
 * servers until the button is actually used.
 */
function GoogleMark(props: React.ComponentProps<"svg">) {
  return (
    <svg viewBox="0 0 48 48" aria-hidden {...props}>
      <path
        fill="#FFC107"
        d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"
      />
      <path
        fill="#FF3D00"
        d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"
      />
      <path
        fill="#4CAF50"
        d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"
      />
      <path
        fill="#1976D2"
        d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"
      />
    </svg>
  );
}

/** Brand-tinted ambience. Purely decorative, so it is hidden from assistive tech. */
function BackgroundGlow() {
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
      <div className="brand-gradient absolute -top-40 left-1/2 size-[36rem] -translate-x-1/2 rounded-full opacity-15 blur-3xl" />
      <div className="brand-gradient absolute -bottom-52 left-1/4 size-[28rem] rounded-full opacity-10 blur-3xl" />
    </div>
  );
}
